import { cn } from '@/lib/utils';
import type { PropsWithChildren } from 'react';

export type PageShellWidth = 'narrow' | 'default' | 'wide' | 'full';

const WIDTH_CLASSES: Record<PageShellWidth, string> = {
    narrow: 'max-w-4xl',
    default: 'max-w-6xl',
    wide: 'max-w-7xl',
    full: 'max-w-none',
};

type PageShellProps = PropsWithChildren<{
    width?: PageShellWidth;
    /**
     * Fills the shell's remaining height instead of growing the page. The page
     * becomes a `min-h-0 flex-1` child of the app shell's flex column, so it
     * sizes itself under the mobile header and the trial banner — a viewport
     * unit here would double-count both. For pages whose own content scrolls
     * (a chat thread, a split pane), never for ordinary stacked pages.
     */
    fill?: boolean;
    className?: string;
    contentClassName?: string;
}>;

export default function PageShell({
    children,
    width = 'wide',
    fill = false,
    className,
    contentClassName,
}: PageShellProps) {
    return (
        <div
            className={cn(
                'relative bg-background',
                fill ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : 'min-h-screen',
                className,
            )}
        >
            <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-72" />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-32 h-80"
            />
            <div
                className={cn(
                    'relative mx-auto flex w-full flex-col gap-5 p-4 sm:gap-8 sm:p-8 lg:px-10',
                    WIDTH_CLASSES[width],
                    fill && 'min-h-0 flex-1 overflow-hidden',
                    contentClassName,
                )}
            >
                {children}
            </div>
        </div>
    );
}
